import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from "../../api/Base";

// Third-party packages.
import TableCSVExport from '../../component/TableCSVExport';
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Types
import Recommendation from '../../types/Recommendation';

// Helpers
import { fetchResults, getDefaultPageSize } from "../../helpers/utils";
import RecommendationApi from '../../api/RecommendationApi';
import { showError } from '../../utils/showAlert';

// Components.
import ReactTable from "../../component/ReactTable";
import ConditionalRender from "../../helpers/ConditionalRender";
import SearchInput from '../../component/SearchInput';

function List() {
    const [tableFilter, setTableFilter] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<Recommendation[]>([]);
    const [recommendationType, setRecommendationType] = useState<string>('open');

    const loadData = async () => {
        setLoading(true);
        const params = {
            open: recommendationType === 'open',
            closed: recommendationType === 'closed',
            hidden: recommendationType === 'hidden',
        };
        const urlParams = new URLSearchParams(
                Object.entries(params).map(([key, value]) => [key, value.toString()])
        ).toString();

        try {
            const result: any[][] = await fetchResults([RecommendationApi.fetch(urlParams)]);
            setLoading(false);
            setData(result[0]);
        } catch (error: any) {
            console.error(error.message);
            const errorMessage = error.message || error.toString();
            setError(errorMessage);
            showError('Failed to load recommendations', errorMessage);
            setData([]);
            setLoading(false)
        }
    };

    useEffect(() => {
        void loadData();
    }, [recommendationType]);


    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const tableData = useMemo(() => data.filter((row) =>
                    row.category?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                    row.action?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                    row.label?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                    row.description?.toLowerCase().includes(tableFilter.toLowerCase())),
            [data, tableFilter]);

    const closeHide = async (id: number, isReset: boolean, isClose: boolean) => {
        setLoading(true);

        const url = `/recommendation`;

        // Update the relevant item in the array
        const updatedData = data.map(item =>
                item.id === id
                        ? {
                            ...item,
                            closed: isReset ? false : isClose || item.closed,
                            hidden: isReset ? false : !isClose || item.hidden,
                        }
                        : item
        );
        try {
            const result = await api.put(url, updatedData);
            if (result.status === 200) {
                await loadData();
            }
        } catch (error: any) {
            console.error("Failed to update recommendation:", error);
            const errorMessage = error.response?.data?.message || error.message || error.toString();
            showError('Failed to update recommendation', errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            accessor: 'link',
            disableSortBy: true
        },
        {
            Header: 'Category',
            accessor: 'category',
        },
        {
            Header: 'Label',
            accessor: 'label',
        },
        {
            Header: 'Description',
            accessor: 'description',
        },
        {
            Header: 'Action',
            accessor: 'action',
            className: 'text-start',
            disableSortBy: true,
            Cell: ({ cell }) => {
                const action = cell.row.values.action;
                const link = cell.row.values.link;

                const isExternal = action.includes("docs.dbmarlin");

                return (
                    <Link
                        className="text-nowrap"
                        rel={isExternal ? "noopener noreferrer" : undefined}
                        to={link}
                    >
                        {action}
                    </Link>
                );
            }
        },
        {
            Header: recommendationType === 'hidden'
                    ? 'Unhide'
                    : recommendationType === 'closed'
                            ? 'Unresolve'
                            : 'Resolve/Hide',
            accessor: 'resolve',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }: any) => (
                    <React.Fragment>
                        <ConditionalRender if={recommendationType !== 'closed' && recommendationType !== 'hidden'}>
                            <button disabled={loading} type="button" className="btn btn-sm btn-success"
                                    onClick={() => closeHide(cell.row.values.id, false, true)}>
                                <i className="fa-regular fa-circle-exclamation-check"></i><span>Mark as resolved</span>
                            </button>
                            <button disabled={loading} type="button" className="btn btn-sm btn-primary"
                                    onClick={() => closeHide(cell.row.values.id, false, false)}>
                                <i className="fa-regular fa-eye-slash"></i><span>Hide</span>
                            </button>
                        </ConditionalRender>

                        <ConditionalRender if={recommendationType === 'closed'}>
                            <button disabled={loading} type="button" className="btn btn-sm btn-success"
                                    onClick={() => closeHide(cell.row.values.id, true, true)}>
                                <i className="fa-regular fa-circle-exclamation-check"></i><span>Mark as unresolved</span>
                            </button>
                        </ConditionalRender>

                        <ConditionalRender if={recommendationType === 'hidden'}>
                            <button disabled={loading} type="button" className="btn btn-sm btn-primary"
                                    onClick={() => closeHide(cell.row.values.id, true, true)}>
                                <i className="fa-regular fa-eye"></i><span>Unhide</span>
                            </button>
                        </ConditionalRender>
                    </React.Fragment>
            )
        }
    ], [tableData]);

    const tableInstance = useTable({
                columns,
                data: tableData,
                initialState: {
                    hiddenColumns: ['id', 'link'],
                    pageIndex: 0,
                    pageSize: getDefaultPageSize()
                },
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetSelectedRows: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
            },
            useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    const getLinkClass = (type: string) =>
            `${recommendationType === type ? 'active' : ''} pointer`;

    return (
            <div className="col h-100">
                <div id="menu-top" className="row row-cols-auto menu-top" role="tablist">
                    <button type="button" onClick={() => setRecommendationType('open')} className={getLinkClass('open')}>Open</button>
                    <button type="button" onClick={() => setRecommendationType('closed')} className={getLinkClass('closed')}>Resolved</button>
                    <button type="button" onClick={() => setRecommendationType('hidden')} className={getLinkClass('hidden')}>Hidden</button>

                </div>
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-file-certificate fa-fw mr-10" aria-hidden="true"></i>
                        Recommendations
                        <span className="badge bg-info"
                              data-tooltip-id="global-tooltip" data-tooltip-content="Total snapshot count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseRecommendations" aria-expanded="false" aria-controls="collapseRecommendations"></i>
                    </div>
                    <div id="collapseRecommendations" className="card-body collapse show">
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                                <TableCSVExport
                                    tableInstance={tableInstance}
                                    filename="Recommendations"
                                />
                            </div>
                            <div className="col col-md-2">
                                <SearchInput
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                                    autoFocus
                                />
                            </div>
                        </div>


                        <ReactTable
                            getTableProps={getTableProps}
                            getTableBodyProps={getTableBodyProps}
                            headerGroups={headerGroups}
                            prepareRow={prepareRow}
                            page={page}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            gotoPage={gotoPage}
                            nextPage={nextPage}
                            previousPage={previousPage}
                            setPageSize={setPageSize}
                            pageIndex={pageIndex}
                            pageSize={pageSize}
                            dataLength={data.length}
                            error={error}
                            loading={loading}
                        />
                    </div>
                </div>
            </div>
    )
}

export default List;
